/**
 * Footer section — BUILDER_DESIGN_SPEC Section 5: background #0f0f14; vertical
 * padding follows the brand Section spacing token (--nos-space-section-y).
 * Section 15: paragraph (light) 15px/400, rgba(255,255,255,0.8); social-icons 12px/400, rgba(255,255,255,0.4).
 *
 * NOS-218: field resolution (tagline, quick_links, social_links, contact, logo
 * override) is shared with PublicFooter via src/lib/footerContent.ts so the
 * builder canvas shows exactly what will publish.
 *
 * NOS-217: background comes from footer_content.background (fallback #0f0f14);
 * foreground colors are luminance-derived (footerForeground) and the logo
 * renders via FooterLogo (luminance-aware contrast chip) — identical to the
 * public/preview footer.
 *
 * NOS-234: vertical padding honors the panel's Compact/Normal/Spacious control
 * (content._styles.padding), and the movable groups (quick links / connect /
 * contact) render in their assigned columns (footer_content.column_layout).
 */
import type { ReactNode } from "react"
import {
  resolveFooterContent,
  footerVerticalPadding,
  socialIconForLabel,
  telHref,
  type FooterMovableGroup,
} from "@/lib/footerContent"
import { FooterLogo } from "@/components/public/FooterLogo"
import type { SectionProps } from "../types"
import type { Organization } from "../types"

interface FooterSectionProps extends SectionProps {
  org?: Organization | null
  /** True in the builder canvas (kept for renderer parity; padding is identical in both contexts) */
  inBuilder?: boolean
}

const GROUP_HEADING_STYLE = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase" as const,
  letterSpacing: "0.06em",
  color: "var(--nos-color-primary, #3b82f6)",
}

export function FooterSection({
  content,
  customizations,
  org,
  styleOverrides,
}: FooterSectionProps) {
  const logoScale = (customizations?.logo_scale as number | undefined) ?? 1.0
  const resolved = resolveFooterContent(content ?? null, {
    orgLogoUrl: org?.logo_url,
    missionStatement: org?.mission_statement,
  })
  const quickLinks = resolved.quickLinks ?? []
  const { socialLinks, contact, fg, columnLayout, subscribe } = resolved
  const year = new Date().getFullYear()
  const paddingY = footerVerticalPadding(content ?? null)

  // NOS-234: contact renders inside the Connect group when they share a column
  // (the historical layout); moved elsewhere it gets its own heading.
  const contactWithConnect = contact != null && columnLayout.contact === columnLayout.connect

  const contactRows = contact && (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {contact.email && (
        <p>
          <a
            href={`mailto:${contact.email}`}
            style={{ fontSize: 14, fontWeight: 400, color: fg.muted, textDecoration: "none" }}
          >
            {contact.email}
          </a>
        </p>
      )}
      {contact.phone && (
        <p>
          <a
            href={telHref(contact.phone)}
            style={{ fontSize: 14, fontWeight: 400, color: fg.muted, textDecoration: "none" }}
          >
            {contact.phone}
          </a>
        </p>
      )}
      {contact.address && (
        <p style={{ fontSize: 14, fontWeight: 400, color: fg.muted }}>{contact.address}</p>
      )}
    </div>
  )

  const groupRenderers: Record<FooterMovableGroup, () => ReactNode> = {
    quick_links: () => (
      <div key="quick_links">
        <p style={GROUP_HEADING_STYLE}>Quick links</p>
        <ul className="mt-4 space-y-2">
          {quickLinks.length > 0 ? (
            quickLinks.map((link) => (
              <li key={link.label}>
                <a
                  href={link.href}
                  style={{
                    fontSize: 15,
                    fontWeight: 400,
                    color: fg.body,
                    textDecoration: "none",
                  }}
                >
                  {link.label}
                </a>
              </li>
            ))
          ) : (
            <li>
              <span style={{ fontSize: 13, color: fg.hint, fontStyle: "italic" }}>
                Add links in the footer panel
              </span>
            </li>
          )}
        </ul>
      </div>
    ),
    connect: () => (
      <div key="connect">
        <p style={GROUP_HEADING_STYLE}>Connect</p>
        {socialLinks.length > 0 ? (
          <div
            style={{
              marginTop: 16,
              display: "flex",
              flexWrap: "wrap",
              gap: 18,
            }}
          >
            {socialLinks.map((link) => {
              const IconComp = socialIconForLabel(link.label)
              return (
                <a
                  key={`${link.label}-${link.url}`}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: 12,
                    fontWeight: 400,
                    color: fg.icon,
                    textDecoration: "none",
                  }}
                  aria-label={link.label}
                >
                  {IconComp ? (
                    <IconComp className="h-5 w-5 @sm:h-6 @sm:w-6" strokeWidth={2} />
                  ) : (
                    <span style={{ textDecoration: "underline" }}>{link.label}</span>
                  )}
                </a>
              )
            })}
          </div>
        ) : (
          !contactWithConnect && (
            <p style={{ marginTop: 16, fontSize: 13, color: fg.hint, fontStyle: "italic" }}>
              Add social links in the footer panel
            </p>
          )
        )}
        {contactWithConnect && <div style={{ marginTop: 16 }}>{contactRows}</div>}
      </div>
    ),
    contact: () =>
      contactWithConnect || !contact ? null : (
        <div key="contact">
          <p style={GROUP_HEADING_STYLE}>Contact</p>
          <div style={{ marginTop: 16 }}>{contactRows}</div>
        </div>
      ),
    // NOS-232: imported newsletter block — canvas shows a static preview; the
    // public surface renders the live NOS email capture.
    subscribe: () =>
      !subscribe ? null : (
        <div key="subscribe" data-footer-subscribe>
          <p style={{ fontSize: 15, fontWeight: 600, color: fg.heading, lineHeight: 1.4 }}>
            {subscribe.heading}
          </p>
          {subscribe.body && (
            <p style={{ marginTop: 6, fontSize: 13, color: fg.body, lineHeight: 1.6 }}>
              {subscribe.body}
            </p>
          )}
          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <input
              type="email"
              placeholder={subscribe.placeholder}
              readOnly
              disabled
              style={{
                flex: 1,
                minWidth: 0,
                padding: "8px 12px",
                borderRadius: 8,
                border: `1px solid ${fg.borderStrong}`,
                background: "transparent",
                color: fg.body,
                fontSize: 13,
              }}
            />
            <button
              type="button"
              disabled
              style={{
                padding: "8px 14px",
                borderRadius: 8,
                border: "none",
                background: "var(--nos-color-primary, #3b82f6)",
                color: "#fff",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {subscribe.cta_text}
            </button>
          </div>
        </div>
      ),
  }

  const groupsIn = (col: 1 | 2 | 3): ReactNode[] =>
    (Object.keys(groupRenderers) as FooterMovableGroup[])
      .filter((g) => columnLayout[g] === col)
      .map((g) => groupRenderers[g]())
      .filter(Boolean)

  const brandBlock = (
    <div key="brand">
      {resolved.logoUrl ? (
        <FooterLogo
          src={resolved.logoUrl}
          alt={org?.name ?? "Organization logo"}
          height={Math.round(40 * logoScale)}
          isUserOverride={resolved.logoIsOverride}
          backgroundIsDark={resolved.backgroundIsDark}
          treatment={resolved.logoTreatment}
        />
      ) : (
        org?.name && (
          <p
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: fg.heading,
              lineHeight: 1.5,
            }}
          >
            {org.name}
          </p>
        )
      )}
      {resolved.tagline && (
        <p
          style={{
            marginTop: 8,
            fontSize: 15,
            fontWeight: 400,
            color: fg.body,
            lineHeight: 1.7,
            maxWidth: 520,
          }}
        >
          {resolved.tagline}
        </p>
      )}
    </div>
  )

  const columnStyle = { display: "flex", flexDirection: "column" as const, gap: 32 }

  return (
    <footer
      className={["px-6 @sm:px-8 @md:px-10", styleOverrides?.className].filter(Boolean).join(" ")}
      style={{
        background: resolved.background,
        paddingTop: paddingY,
        paddingBottom: paddingY,
        ...(styleOverrides?.style ?? {}),
      }}
    >
      <div className="mx-auto" style={{ maxWidth: 1152 }}>
        <div className="grid gap-10 @sm:grid-cols-2 @md:grid-cols-3 @md:gap-12">
          {/* Column 1: brand (fixed) + any groups assigned to column 1 */}
          <div style={columnStyle}>
            {brandBlock}
            {groupsIn(1)}
          </div>
          <div style={columnStyle}>{groupsIn(2)}</div>
          <div style={columnStyle}>{groupsIn(3)}</div>
        </div>
        <div
          className="flex flex-col items-center justify-between gap-4 @sm:flex-row"
          style={{
            marginTop: 32,
            paddingTop: 24,
            borderTop: `1px solid ${fg.borderStrong}`,
          }}
        >
          <p
            style={{
              fontSize: 13,
              fontWeight: 400,
              color: fg.subtle,
            }}
          >
            © {year} {org?.name ?? "Organization"}
          </p>
          <p
            style={{
              fontSize: 12,
              fontWeight: 400,
              color: fg.faint,
            }}
          >
            <a
              href="https://getnos.ai"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "inherit",
                textDecoration: "none",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.textDecoration = "underline"
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.textDecoration = "none"
              }}
            >
              Powered by NOS
            </a>
          </p>
        </div>
      </div>
    </footer>
  )
}
